import React from 'react';
import { clsx } from 'clsx';

interface FieldProps {
  /** Uppercase muted label. */
  label: React.ReactNode;
  /** Grey parenthetical appended to the label (normal case). */
  sublabel?: React.ReactNode;
  /** Pre-formatted value (a Figure/SignedValue node or a plain string). */
  value?: React.ReactNode;
  /** Faint helper line under the value. */
  hint?: React.ReactNode;
  /** Honest empty: render a faint placeholder instead of a value. */
  empty?: boolean;
  /** Placeholder shown when `empty` ("Not set", "rating not available", …). */
  emptyLabel?: React.ReactNode;
  /** Monospace value (ISIN, account numbers). */
  mono?: boolean;
  /**
   * Value emphasis tier:
   *   - 'big'  — section-lead size (Quantity, Contract / Closing value totals).
   *   - 'mid'  — promoted but a notch below 'big' (a settlement-ccy per-share
   *              price, which must read as primary without shouting as loud as a
   *              million-unit total).
   *   - 'default' — the standard label/value weight.
   */
  size?: 'default' | 'mid' | 'big';
  /**
   * Subordinate line rendered beneath the value in a smaller, muted weight — the
   * cross-currency security-ccy original sitting under the settlement-ccy primary
   * (LR-474). Resets the value's size/weight so a 'big' primary keeps a quiet
   * reference line below it.
   */
  secondary?: React.ReactNode;
  title?: string;
  className?: string;
  /**
   * DOM semantic for label / value:
   *   - 'div' (default) — plain `<div>` pair; safe inside any container.
   *   - 'definition'    — `<dt>` / `<dd>` pair; renders the field as a definition
   *                       list entry. The CALLER must wrap the group in `<dl>`
   *                       (TradeTermsTab uses this for the "deal facts" sheet —
   *                       LR-208 r3 L2 unifies the formerly-inline Term Field
   *                       onto this primitive).
   */
  as?: 'div' | 'definition';
}

/**
 * Label / value / hint datum for the Trade Terms deal map and cards. The
 * `empty` variant renders an honest faint placeholder rather than a bare value
 * (DESIGN-SPEC §2 "honest empty states"). Values are pre-formatted by the
 * caller (Figure/SignedValue carry the no-float HON-03 guarantee).
 *
 * Single source for both the "definition list" layout (TradeTermsTab) and the
 * generic label/value blocks (AdequacyCard, …); the `as` prop picks the DOM
 * semantic without splitting the visual contract.
 */
export function Field({
  label,
  sublabel,
  value,
  hint,
  empty = false,
  emptyLabel = '—',
  mono = false,
  size = 'default',
  secondary,
  title,
  className,
  as = 'div',
}: FieldProps) {
  const valueClass = mono
    ? 'font-mono text-[13px]'
    : size === 'big'
      ? 'text-[19px] font-bold tracking-[-0.3px]'
      : size === 'mid'
        ? 'text-[16px] font-semibold tracking-[-0.2px]'
        : 'text-[14.5px] font-medium';

  const Label: React.ElementType = as === 'definition' ? 'dt' : 'div';
  const Value: React.ElementType = as === 'definition' ? 'dd' : 'div';

  return (
    <div className={className}>
      <Label className="text-[11px] uppercase tracking-[0.5px] font-semibold text-lr-muted mb-[3px]">
        {label}
        {sublabel && (
          <span className="ml-1 normal-case tracking-normal font-normal text-lr-faint">
            {sublabel}
          </span>
        )}
      </Label>
      {empty ? (
        // Keep the `title` tooltip on the empty branch too (LR-208 r6 LOW): an
        // honest-empty field can still carry an explanatory hover (e.g. "ISIN not
        // provided"), which was previously dropped only when `empty` was set.
        <Value className="lr-num text-[14.5px] text-lr-faint" title={title}>
          {emptyLabel}
        </Value>
      ) : (
        <Value className={clsx('lr-num text-lr-fg', valueClass)} title={title}>
          {value}
          {secondary != null && (
            <span className="mt-0.5 block text-[12.5px] font-normal tracking-normal text-lr-muted">
              {secondary}
            </span>
          )}
        </Value>
      )}
      {hint && <div className="text-[11.5px] text-lr-faint mt-[2px]">{hint}</div>}
    </div>
  );
}
